/**
 * Product Priority Badge
 *
 * Inline-editable priority badge for the product item drawer — click to pick a
 * priority (or clear it). Mirrors the task board's PriorityEditableBadge.
 *
 * @module features/product-board/components/ProductPriorityBadge
 */

import { EditableBadge, type BadgeProps, type BadgeVariant } from '@chatsdet/ui';
import { useUpdateProductItem } from '../hooks/useProductItems';
import { PRIORITY_LABELS, PRIORITY_OPTIONS } from '../constants';
import type { ProductItem, ProductItemPriority } from '../types';

const PRIORITY_VARIANTS: Record<string, BadgeVariant> = {
  none: 'outline',
  p0: 'destructive',
  p1: 'warning',
  p2: 'secondary',
  p3: 'outline',
};

interface ProductPriorityBadgeProps {
  item: ProductItem;
  includeDeprecated: boolean;
  size?: BadgeProps['size'];
}

export function ProductPriorityBadge({ item, includeDeprecated, size }: ProductPriorityBadgeProps) {
  const updateItem = useUpdateProductItem(includeDeprecated);
  const value = item.priority ?? 'none';

  const handleChange = (next: string) => {
    const priority = next === 'none' ? null : (next as ProductItemPriority);
    if (priority === item.priority) return;
    updateItem.mutate({ id: item.id, priority });
  };

  return (
    <EditableBadge
      value={value}
      options={PRIORITY_OPTIONS}
      onChange={handleChange}
      getVariant={(v) => PRIORITY_VARIANTS[v] ?? 'outline'}
      formatLabel={(v) => (v === 'none' ? 'No priority' : PRIORITY_LABELS[v as ProductItemPriority])}
      size={size}
      isLoading={updateItem.isPending}
      aria-label={`Change priority for ${item.title}`}
    />
  );
}
